/**
 * Sidebar Provider
 * Manages sidebar state globally with responsive behavior
 *
 * @shared - This provider is shared across all portals
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'

type SidebarState = 'expanded' | 'collapsed' | 'hidden'

interface SidebarContextValue {
  /** Current sidebar state */
  state: SidebarState
  /** Whether mobile drawer is open */
  mobileOpen: boolean
  /** Whether viewport is mobile (<768px) */
  isMobile: boolean
  /** Whether viewport is tablet (768-1024px) */
  isTablet: boolean
  /** Toggle sidebar collapsed/expanded on desktop */
  toggle: () => void
  /** Open mobile drawer */
  openMobile: () => void
  /** Close mobile drawer */
  closeMobile: () => void
  /** Collapse sidebar */
  collapse: () => void
  /** Expand sidebar */
  expand: () => void
}

const SidebarContext = createContext<SidebarContextValue | null>(null)

const MOBILE_BREAKPOINT = 768
const TABLET_BREAKPOINT = 1024

interface SidebarProviderProps {
  children: ReactNode
  /** Default state for desktop */
  defaultState?: SidebarState
  /** Storage key for persisting sidebar state */
  storageKey?: string
}

export function SidebarProvider({
  children,
  defaultState = 'expanded',
  storageKey = 'spcc-sidebar-state'
}: SidebarProviderProps) {
  const [state, setState] = useState<SidebarState>(() => {
    if (typeof window === 'undefined') return defaultState
    const saved = localStorage.getItem(storageKey)
    return (saved as SidebarState) || defaultState
  })
  const [mobileOpen, setMobileOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)

  // Handle viewport changes
  useEffect(() => {
    const checkViewport = () => {
      const width = window.innerWidth
      const newIsMobile = width < MOBILE_BREAKPOINT
      const newIsTablet = width >= MOBILE_BREAKPOINT && width < TABLET_BREAKPOINT

      setIsMobile(newIsMobile)
      setIsTablet(newIsTablet)

      // Auto-collapse on tablet, hide on mobile
      if (newIsMobile) {
        setState('hidden')
        setMobileOpen(false)
      } else if (newIsTablet) {
        setState('collapsed')
      } else {
        // Restore saved state on desktop
        const saved = localStorage.getItem(storageKey)
        if (saved && (saved === 'expanded' || saved === 'collapsed')) {
          setState(saved)
        }
      }
    }

    checkViewport()
    window.addEventListener('resize', checkViewport)
    return () => window.removeEventListener('resize', checkViewport)
  }, [storageKey])

  // Persist state changes
  useEffect(() => {
    if (!isMobile && !isTablet && state !== 'hidden') {
      localStorage.setItem(storageKey, state)
    }
  }, [state, isMobile, isTablet, storageKey])

  // Close mobile drawer on route change
  useEffect(() => {
    const handleRouteChange = () => {
      if (mobileOpen) setMobileOpen(false)
    }
    window.addEventListener('popstate', handleRouteChange)
    return () => window.removeEventListener('popstate', handleRouteChange)
  }, [mobileOpen])

  const toggle = useCallback(() => {
    setState((prev) => (prev === 'expanded' ? 'collapsed' : 'expanded'))
  }, [])

  const collapse = useCallback(() => setState('collapsed'), [])
  const expand = useCallback(() => setState('expanded'), [])
  const openMobile = useCallback(() => setMobileOpen(true), [])
  const closeMobile = useCallback(() => setMobileOpen(false), [])

  return (
    <SidebarContext.Provider
      value={{
        state,
        mobileOpen,
        isMobile,
        isTablet,
        toggle,
        openMobile,
        closeMobile,
        collapse,
        expand,
      }}
    >
      {children}
    </SidebarContext.Provider>
  )
}

export function useSidebar() {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}
